import { z } from 'zod';

/** Two-letter US state or DC code used for report customer map scope. */
export const UsStateCodeSchema = z
  .string()
  .length(2)
  .regex(/^[A-Z]{2}$/);

/** Parsed US state code. */
export type UsStateCode = z.infer<typeof UsStateCodeSchema>;

/**
 * Normalizes a warehouse `client_state` value into a map-scope US state code.
 * @param raw - Two-letter state from `dw.clients.client_state` (any case).
 * @returns Uppercase code, or null when missing or not a US/DC code.
 */
export const parseWarehouseUsStateCode = (
  raw: string | null | undefined,
): UsStateCode | null => {
  const parsed = UsStateCodeSchema.safeParse(raw?.trim().toUpperCase() ?? '');
  return parsed.success ? parsed.data : null;
};

/** US states and DC for customer admin and map filtering (code → label). */
export const US_STATE_OPTIONS: ReadonlyArray<{
  code: UsStateCode;
  label: string;
}> = [
  { code: 'AL', label: 'Alabama' },
  { code: 'AK', label: 'Alaska' },
  { code: 'AZ', label: 'Arizona' },
  { code: 'AR', label: 'Arkansas' },
  { code: 'CA', label: 'California' },
  { code: 'CO', label: 'Colorado' },
  { code: 'CT', label: 'Connecticut' },
  { code: 'DE', label: 'Delaware' },
  { code: 'DC', label: 'District of Columbia' },
  { code: 'FL', label: 'Florida' },
  { code: 'GA', label: 'Georgia' },
  { code: 'HI', label: 'Hawaii' },
  { code: 'ID', label: 'Idaho' },
  { code: 'IL', label: 'Illinois' },
  { code: 'IN', label: 'Indiana' },
  { code: 'IA', label: 'Iowa' },
  { code: 'KS', label: 'Kansas' },
  { code: 'KY', label: 'Kentucky' },
  { code: 'LA', label: 'Louisiana' },
  { code: 'ME', label: 'Maine' },
  { code: 'MD', label: 'Maryland' },
  { code: 'MA', label: 'Massachusetts' },
  { code: 'MI', label: 'Michigan' },
  { code: 'MN', label: 'Minnesota' },
  { code: 'MS', label: 'Mississippi' },
  { code: 'MO', label: 'Missouri' },
  { code: 'MT', label: 'Montana' },
  { code: 'NE', label: 'Nebraska' },
  { code: 'NV', label: 'Nevada' },
  { code: 'NH', label: 'New Hampshire' },
  { code: 'NJ', label: 'New Jersey' },
  { code: 'NM', label: 'New Mexico' },
  { code: 'NY', label: 'New York' },
  { code: 'NC', label: 'North Carolina' },
  { code: 'ND', label: 'North Dakota' },
  { code: 'OH', label: 'Ohio' },
  { code: 'OK', label: 'Oklahoma' },
  { code: 'OR', label: 'Oregon' },
  { code: 'PA', label: 'Pennsylvania' },
  { code: 'RI', label: 'Rhode Island' },
  { code: 'SC', label: 'South Carolina' },
  { code: 'SD', label: 'South Dakota' },
  { code: 'TN', label: 'Tennessee' },
  { code: 'TX', label: 'Texas' },
  { code: 'UT', label: 'Utah' },
  { code: 'VT', label: 'Vermont' },
  { code: 'VA', label: 'Virginia' },
  { code: 'WA', label: 'Washington' },
  { code: 'WV', label: 'West Virginia' },
  { code: 'WI', label: 'Wisconsin' },
  { code: 'WY', label: 'Wyoming' },
] as const;

/** Census state FIPS (2-digit string) by {@link UsStateCode}. */
export const US_STATE_FIPS_BY_CODE: Record<UsStateCode, string> = {
  AL: '01',
  AK: '02',
  AZ: '04',
  AR: '05',
  CA: '06',
  CO: '08',
  CT: '09',
  DE: '10',
  DC: '11',
  FL: '12',
  GA: '13',
  HI: '15',
  ID: '16',
  IL: '17',
  IN: '18',
  IA: '19',
  KS: '20',
  KY: '21',
  LA: '22',
  ME: '23',
  MD: '24',
  MA: '25',
  MI: '26',
  MN: '27',
  MS: '28',
  MO: '29',
  MT: '30',
  NE: '31',
  NV: '32',
  NH: '33',
  NJ: '34',
  NM: '35',
  NY: '36',
  NC: '37',
  ND: '38',
  OH: '39',
  OK: '40',
  OR: '41',
  PA: '42',
  RI: '44',
  SC: '45',
  SD: '46',
  TN: '47',
  TX: '48',
  UT: '49',
  VT: '50',
  VA: '51',
  WA: '53',
  WV: '54',
  WI: '55',
  WY: '56',
};

const US_STATE_LABEL_BY_CODE = new Map(
  US_STATE_OPTIONS.map(option => [option.code, option.label]),
);

/**
 * Resolves a US state code to its display label for report copy.
 * @param usStateCode - Two-letter state code from the customer record.
 * @param fallback - Label when the code is missing or unknown.
 * @returns State name for narrative copy (e.g. "Michigan").
 */
export const resolveUsStateLabel = (
  usStateCode: string | null | undefined,
  fallback = 'your community',
): string => {
  if (!usStateCode?.trim()) {
    return fallback;
  }
  const code = usStateCode.trim().toUpperCase() as UsStateCode;
  return US_STATE_LABEL_BY_CODE.get(code) ?? fallback;
};
